import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, money, jobTime } from "@/components/status";
import JobChat from "@/components/JobChat";
import { CleanerBidForm, PMBidList } from "@/components/BidPanel";
import { ScheduleActions } from "@/components/ScheduleActions";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, MapPin, Calendar, Clock, DollarSign, Loader2, Check, X, Play,
  CheckCircle2, Upload, Star, AlertTriangle, Camera,
} from "lucide-react";
import { toast } from "sonner";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checklist, setChecklist] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [reason, setReason] = useState("");
  const [review, setReview] = useState({ rating: 5, comment: "" });
  const [fees, setFees] = useState({ host_fee_pct: 8, cleaner_fee_pct: 8 });

  const load = useCallback(async () => {
    const { data } = await api.get(`/jobs/${id}`);
    setJob(data);
    setChecklist(data.checklist || []);
    setPhotos(data.photos || []);
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings").then((r) => setFees(r.data)).catch(() => {}); }, []);

  if (!job) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-ocean" /></div>;

  const isCleaner = user.role === "cleaner";
  const isPM = user.role === "pm";
  const isMine = job.cleaner?.id === user.id;

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleItem = (i) => {
    const next = checklist.map((c, x) => (x === i ? { ...c, done: !c.done } : c));
    setChecklist(next);
    api.put(`/jobs/${id}/checklist`, { checklist: next }).catch(() => {});
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => setPhotos((p) => [...p, reader.result]);
      reader.readAsDataURL(f);
    });
  };

  const cleanerActions = () => {
    if (job.pricing_mode === "bidding" && (job.status === "open" || (job.status !== "open" && !isMine)))
      return <CleanerBidForm job={job} onChanged={load} />;
    if (job.status === "open" || job.status === "pending")
      return (
        <div className="space-y-2">
          <Button className="w-full bg-ocean hover:bg-ocean/90" disabled={busy}
            onClick={() => act(() => api.post(`/jobs/${id}/accept`), "Job accepted")} data-testid="accept-job">
            <Check className="w-4 h-4 mr-1" /> Accept Job
          </Button>
          {job.status === "pending" && (
            <Button variant="outline" className="w-full" disabled={busy}
              onClick={() => act(() => api.post(`/jobs/${id}/decline`), "Declined")} data-testid="decline-job">
              <X className="w-4 h-4 mr-1" /> Decline
            </Button>
          )}
        </div>
      );
    if (!isMine) return null;
    if (job.status === "accepted")
      return (
        <Button className="w-full bg-ocean hover:bg-ocean/90" disabled={busy}
          onClick={() => act(() => api.post(`/jobs/${id}/start`), "Job started")} data-testid="start-job">
          <Play className="w-4 h-4 mr-1" /> Start Cleaning
        </Button>
      );
    if (job.status === "in_progress")
      return (
        <Button className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={busy}
          onClick={() => act(() => api.post(`/jobs/${id}/complete`, { photos, checklist }), "Marked complete")} data-testid="complete-job">
          <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Complete
        </Button>
      );
    return null;
  };

  const payJob = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${id}/pay`, { origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success("Payment complete — cleaner paid");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pmActions = () => {
    if (job.status === "completed")
      return (
        <div className="space-y-2">
          <Button className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={busy}
            onClick={() => act(() => api.post(`/jobs/${id}/approve`), "Work approved — ready to pay")} data-testid="approve-job">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Approve Work
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full text-signal border-signal hover:bg-signal hover:text-white" data-testid="open-dispute">
                <AlertTriangle className="w-4 h-4 mr-1" /> Open Dispute
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle className="font-heading">Open Dispute</DialogTitle></DialogHeader>
              <Label className="uppercase text-xs tracking-[0.2em]">Reason</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Describe the issue…" data-testid="dispute-reason" />
              <DialogFooter>
                <Button className="bg-signal hover:bg-signal/90" disabled={busy || !reason}
                  onClick={() => act(() => api.post("/disputes", { job_id: id, reason }), "Dispute opened")} data-testid="submit-dispute">
                  Submit Dispute
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      );
    if (job.status === "approved")
      return (
        <Button className="w-full bg-ocean hover:bg-ocean/90" disabled={busy} onClick={payJob} data-testid="pay-job">
          <DollarSign className="w-4 h-4 mr-1" /> Pay {money(job.payout_amount * (1 + fees.host_fee_pct / 100))}
        </Button>
      );
    if (job.status === "paid")
      return (
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full" data-testid="leave-review"><Star className="w-4 h-4 mr-1" /> Leave Review</Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle className="font-heading">Rate {job.cleaner?.name}</DialogTitle></DialogHeader>
            <div className="flex gap-1 my-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setReview({ ...review, rating: n })} data-testid={`star-${n}`}>
                  <Star className={`w-7 h-7 ${n <= review.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
            <Textarea value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} placeholder="Optional comment…" data-testid="review-comment" />
            <DialogFooter>
              <Button className="bg-ocean hover:bg-ocean/90" disabled={busy}
                onClick={() => act(() => api.post("/reviews", { job_id: id, ...review }), "Review posted")} data-testid="submit-review">
                Post Review
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      );
    if (job.status === "cancelled")
      return <div className="text-sm text-muted-foreground text-center py-2" data-testid="cancelled-note">Cancelled{job.cancel_reason ? ` — ${job.cancel_reason}` : ""}.</div>;
    if (job.status === "open" && job.pricing_mode === "bidding")
      return <div className="text-sm text-muted-foreground text-center py-2">Review offers below and hire a cleaner.</div>;
    return <div className="text-sm text-muted-foreground text-center py-2">Waiting for cleaner action.</div>;
  };

  const roleActions = () => {
    if (isCleaner) return cleanerActions();
    if (isPM) return pmActions();
    return <div className="text-sm text-muted-foreground">Admin view — read only.</div>;
  };

  const doneCount = checklist.filter((c) => c.done).length;

  return (
    <div className="space-y-6" data-testid="job-detail">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-sapphire" data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">{job.property?.name}</h1>
            <StatusBadge status={job.status} />
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-2">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{job.property?.address}</span>
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{job.date}</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{jobTime(job)}</span>
            <span className="flex items-center gap-1 font-semibold text-sapphire"><DollarSign className="w-4 h-4" />{job.pricing_mode === "bidding" && job.status === "open" ? (job.budget_amount > 0 ? `Budget ${money(job.budget_amount)}` : "Open to offers") : money(job.payout_amount)}</span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs defaultValue="overview">
            <TabsList data-testid="detail-tabs">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="checklist" data-testid="tab-checklist">Checklist ({doneCount}/{checklist.length})</TabsTrigger>
              <TabsTrigger value="photos" data-testid="tab-photos">Photos</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-4">
              {job.property?.photos?.[0] && (
                <img src={job.property.photos[0]} alt="Property" className="w-full aspect-[16/9] object-cover rounded-md border border-border" />
              )}
              <div className="border border-border rounded-md bg-white p-4">
                <h3 className="font-heading font-bold text-sapphire mb-2">Instructions</h3>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{job.instructions || "No special instructions."}</p>
                {job.property?.notes && (
                  <>
                    <h3 className="font-heading font-bold text-sapphire mt-4 mb-2">Access Notes</h3>
                    <p className="text-sm text-slate-600">{job.property.notes}</p>
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-border rounded-md bg-white p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Cleaner</div>
                  {job.cleaner ? (
                    <button onClick={() => navigate(`/profile/${job.cleaner.id}`)} className="font-semibold text-ocean hover:underline" data-testid="cleaner-profile-link">
                      {job.cleaner.name}
                    </button>
                  ) : <div className="font-semibold text-sapphire">Unassigned</div>}
                </div>
                <div className="border border-border rounded-md bg-white p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Host</div>
                  <div className="font-semibold text-sapphire">{job.pm?.name}</div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="checklist" className="mt-4">
              <div className="border border-border rounded-md bg-white divide-y divide-border">
                {checklist.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No checklist items.</div>}
                {checklist.map((c, i) => {
                  const editable = isCleaner && isMine && job.status === "in_progress";
                  return (
                    <div key={`${c.text}-${i}`} className="flex items-center gap-3 px-4 py-3" data-testid={`checklist-item-${i}`}>
                      <Checkbox checked={!!c.done} disabled={!editable} onCheckedChange={() => editable && toggleItem(i)} />
                      <span className={`text-sm ${c.done ? "line-through text-muted-foreground" : "text-slate-700"}`}>{c.text}</span>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="photos" className="mt-4 space-y-4">
              {isCleaner && isMine && job.status === "in_progress" && (
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-md py-8 cursor-pointer hover:border-ocean transition-colors" data-testid="photo-upload">
                  <Camera className="w-5 h-5 text-ocean" />
                  <span className="text-sm font-medium text-ocean">Upload completion photos</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} data-testid="photo-input" />
                </label>
              )}
              {photos.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">No photos yet.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {photos.map((p, i) => (
                    <img key={`${i}-${String(p).slice(-16)}`} src={p} alt={`Proof ${i}`} className="aspect-square object-cover rounded-md border border-border" data-testid={`photo-${i}`} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar: actions + chat */}
        <div className="space-y-4">
          <div className="border border-border rounded-md bg-white p-4 sticky top-20">
            <h3 className="font-heading font-bold text-sapphire mb-3">Actions</h3>
            <div className="mb-3 rounded-md bg-slate-50 border border-border p-3 text-sm space-y-1" data-testid="fee-breakdown">
              <div className="flex justify-between"><span className="text-muted-foreground">Job value</span><span className="font-semibold text-sapphire">{money(job.payout_amount)}</span></div>
              {isCleaner ? (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">Platform fee ({fees.cleaner_fee_pct}%)</span><span className="text-signal">−{money(job.payout_amount * fees.cleaner_fee_pct / 100)}</span></div>
                  <div className="flex justify-between border-t border-border pt-1 mt-1"><span className="font-semibold">You earn</span><span className="font-heading font-bold text-emerald-700">{money(job.payout_amount * (1 - fees.cleaner_fee_pct / 100))}</span></div>
                </>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">Service fee ({fees.host_fee_pct}%)</span><span className="text-signal">+{money(job.payout_amount * fees.host_fee_pct / 100)}</span></div>
                  <div className="flex justify-between border-t border-border pt-1 mt-1"><span className="font-semibold">You pay</span><span className="font-heading font-bold text-sapphire">{money(job.payout_amount * (1 + fees.host_fee_pct / 100))}</span></div>
                </>
              )}
            </div>
            {roleActions()}
            {!(isCleaner && !isMine) && <ScheduleActions job={job} role={user.role} onChanged={load} />}
          </div>
          {isPM && job.pricing_mode === "bidding" && <PMBidList job={job} onChanged={load} />}
          {job.history?.length > 0 && (
            <div className="border border-border rounded-md bg-white p-4" data-testid="job-history">
              <h3 className="font-heading font-bold text-sapphire mb-2 text-sm">History</h3>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {job.history.map((h, i) => (
                  <li key={`${h.at}-${i}`}>{new Date(h.at).toLocaleString()} — {h.type.replace(/_/g, " ")}{h.to ? ` → ${h.to}` : ""}{h.reason ? ` (${h.reason})` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          {(job.cleaner && (isPM || (isCleaner && isMine))) && (
            <div className="border border-border rounded-md bg-white p-4">
              <h3 className="font-heading font-bold text-sapphire mb-3">Job Chat</h3>
              <JobChat jobId={job.id} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
